import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@noor/api';
import { toast } from '@noor/lib';
import {
  conversationsApi,
  conversationsKeys,
  type ChatMacro,
  type ConversationId,
  type MacroLang,
} from '@/entities/conversation';

interface MacroBody {
  title: string;
  message: string;
  lang: MacroLang;
}

/** `POST chat/macros`. */
export function useCreateChatMacro() {
  const { t } = useTranslation('inbox');
  const queryClient = useQueryClient();
  return useMutation({
    meta: { suppressErrorToast: true },
    mutationFn: (body: MacroBody) => conversationsApi.createMacro(body),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: conversationsKeys.macros() }),
    onError: (error) => toast.error(getApiErrorMessage(error, t('errors.macroCreate'))),
  });
}

/** `PATCH chat/macros/{id}`. */
export function useUpdateChatMacro() {
  const { t } = useTranslation('inbox');
  const queryClient = useQueryClient();
  return useMutation({
    meta: { suppressErrorToast: true },
    mutationFn: ({ id, body }: { id: ConversationId; body: MacroBody }) =>
      conversationsApi.updateMacro(id, body),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: conversationsKeys.macros() }),
    onError: (error) => toast.error(getApiErrorMessage(error, t('errors.macroUpdate'))),
  });
}

/**
 * `DELETE chat/macros/{id}` — OPTIMISTIC (cpanel dropped the chip at once and
 * rolled back on failure): the macro leaves the cached list in `onMutate`,
 * returns in `onError`, and the server reconciles in `onSettled`.
 */
export function useDeleteChatMacro(lang: MacroLang) {
  const { t } = useTranslation('inbox');
  const queryClient = useQueryClient();
  const key = conversationsKeys.macrosByLang(lang);
  return useMutation({
    meta: { suppressErrorToast: true },
    mutationFn: (id: ConversationId) => conversationsApi.deleteMacro(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: conversationsKeys.macros() });
      const previous = queryClient.getQueryData<ChatMacro[]>(key);
      queryClient.setQueryData<ChatMacro[]>(key, (macros) =>
        macros?.filter((macro) => String(macro.id) !== String(id)),
      );
      return { previous };
    },
    onError: (error, _id, context) => {
      if (context?.previous !== undefined)
        queryClient.setQueryData(key, context.previous);
      toast.error(getApiErrorMessage(error, t('errors.macroDelete')));
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: conversationsKeys.macros() }),
  });
}
